// ./src/components/features/Account/ConfirmEmailToPublish/ConfirmEmailToPublish.tsx
//
// ROADMAP #198 a — "Confirm your email to publish": said where an event is published (the dashboard, the wizard's finish
// screen) BEFORE the host presses Publish, with the way through — the onboarding's email step, which brings them back
// here (`returnUrl`) once the code is in. Shown only while `publisherEmailHeld` (utils/publishGate).
// ⚠ `returnTo` is where publishing HAPPENS — the event's dashboard — never assumed from the URL: the wizard's finish
//   screen lives at /events/create, and coming back there opened a blank wizard (review, 2026-10-21).
// ⚠ Someone with NO address of their own (a crew link) cannot confirm one: they are told who can publish, and sent
//   nowhere — the email step would have nothing to show and bounce them straight back.
import React from "react";
import { Alert, Button, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AppIcon } from "@/components/generic";
import { ONBOARDING_STEP, onboardingStepPath } from "@/components/features/Onboarding/onboardingSteps";

/** Where "Confirm my email" goes from the page at `here` (path + query) — and comes back to. */
export const confirmEmailToPublishPath = (here: string): string => onboardingStepPath(ONBOARDING_STEP.EMAIL, here);

export interface ConfirmEmailToPublishProps {
  /** Where the host comes back to publish (the event's dashboard). */
  returnTo: string;
  /** The publisher has an address to confirm (`!!user.email`). */
  canConfirm: boolean;
  w?: string;
}

export const ConfirmEmailToPublish: React.FC<ConfirmEmailToPublishProps> = ({ returnTo, canConfirm, w }) => {
  const { t } = useTranslation(["events"]);
  const navigate = useNavigate();

  if (!canConfirm) {
    return (
      <Alert variant="light" color="yellow" w={w} icon={<AppIcon iconName="Mail" size={18} />} ta="left">
        <Text size="md">
          {t("events:publishEmail.noAddress", {
            defaultValue: "Publishing needs a signed-in team member whose email address is confirmed — ask the event's owner to publish it.",
          })}
        </Text>
      </Alert>
    );
  }

  return (
    <Alert variant="light" color="yellow" w={w} icon={<AppIcon iconName="Mail" size={18} />} ta="left">
      <Stack gap="sm" align="flex-start">
        <Text size="md" fw={600}>
          {t("events:publishEmail.title", { defaultValue: "Confirm your email address to publish" })}
        </Text>
        <Text size="md">
          {t("events:publishEmail.body", {
            defaultValue: "We send a code to your address — it takes a minute, and you come straight back here to publish.",
          })}
        </Text>
        <Button size="md" variant="light" color="yellow" onClick={() => navigate(confirmEmailToPublishPath(returnTo))}>
          {t("events:publishEmail.action", { defaultValue: "Confirm my email" })}
        </Button>
      </Stack>
    </Alert>
  );
};
